import { Controller } from "@hotwired/stimulus";
import Sortable from "sortablejs";
import TomSelect from "tom-select";

export default class extends Controller {
  static targets = ["orderedInput", "picker", "rankingList"];

  static values = {
    max: Number,
  };

  declare readonly hasMaxValue: boolean;
  declare readonly maxValue: number;
  declare readonly orderedInputTarget: HTMLInputElement;
  declare readonly pickerTarget: HTMLSelectElement;
  declare readonly rankingListTarget: HTMLElement;

  private form: HTMLFormElement | null = null;
  private picker: TomSelect | null = null;
  private sortable: Sortable | null = null;

  private readonly teardown = () => {
    this.picker?.destroy();
    this.picker = null;

    this.sortable?.destroy();
    this.sortable = null;
  };

  connect() {
    this.form = this.element.closest("form");
    this.initializePicker();
    this.selectedIdsFromInput().forEach((id) => {
      this.picker?.addItem(id, true);
    });
    this.initializeSortable();
    this.syncPickerAvailability();
    this.syncBadgeOrder();
    document.addEventListener("turbo:before-cache", this.teardown);
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.teardown);
    this.form = null;
    this.teardown();
  }

  remove(event: Event) {
    const item = (event.currentTarget as HTMLElement).closest("li");
    if (!item) return;

    const recommendationId = item.dataset.recommendationRankerIdValue;
    if (!recommendationId) {
      return;
    }

    item.remove();
    this.picker?.removeItem(recommendationId, true);
    this.updateOrderedInput(
      this.selectedIdsFromInput().filter((id) => id !== recommendationId),
    );
    this.syncBadgeOrder();
    this.submitAutosave();
  }

  private initializePicker() {
    this.picker = new TomSelect(this.pickerTarget, {
      create: false,
      hidePlaceholder: true,
      hideSelected: true,
      maxItems: this.maxSelections(),
      placeholder: "Search recommendations...",
    });

    this.picker.wrapper.classList.add(
      "w-full",
      "rounded-lg",
      "border",
      "border-slate-300",
      "bg-white",
      "px-1",
    );
    this.picker.control.classList.add(
      "!border-0",
      "!bg-transparent",
      "!shadow-none",
      "pl-10",
      "pr-3",
      "py-1",
    );
    this.picker.control_input.classList.add("pl-10", "py-1");

    this.picker.on("change", (values: string | string[]) => {
      this.syncFromPicker(values);
    });
  }

  private initializeSortable() {
    this.sortable = Sortable.create(this.rankingListTarget, {
      animation: 150,
      draggable: "li",
      dragClass: "recommendation-dragging",
      forceFallback: true,
      handle: "[data-recommendation-ranker-handle]",
      onEnd: () => {
        this.updateOrderedInput(this.selectedIdsFromList());
        this.syncBadgeOrder();
        this.submitAutosave();
      },
    });
  }

  private syncFromPicker(values: string | string[]) {
    const newValues = Array.isArray(values)
      ? values
      : values.split(",").filter(Boolean);
    const currentIds = this.selectedIdsFromInput();
    const currentSet = new Set(currentIds);

    const added = newValues.filter((v) => !currentSet.has(v));
    const newSet = new Set(newValues);
    const kept = currentIds.filter((id) => newSet.has(id));

    this.updateOrderedInput([...kept, ...added]);
    this.submitAutosave();
  }

  private maxSelections() {
    return this.hasMaxValue ? this.maxValue : 5;
  }

  private selectedIdsFromInput() {
    return this.orderedInputTarget.value
      .split(",")
      .map((id) => id.trim())
      .filter((id) => id.length > 0);
  }

  private selectedIdsFromList() {
    return Array.from(
      this.rankingListTarget.querySelectorAll<HTMLElement>("li"),
    )
      .map((item) => item.dataset.recommendationRankerIdValue)
      .filter((value): value is string => Boolean(value));
  }

  private syncBadgeOrder() {
    this.rankingListTarget
      .querySelectorAll<HTMLElement>("[data-recommendation-ranker-rank-badge]")
      .forEach((badge, index) => {
        badge.textContent = String(index + 1);
      });
  }

  private syncPickerAvailability() {
    if (this.selectedIdsFromInput().length >= this.maxSelections()) {
      this.picker?.disable();
    } else {
      this.picker?.enable();
    }
  }

  private updateOrderedInput(selectedIds: string[]) {
    this.orderedInputTarget.value = selectedIds.join(",");
    this.syncPickerAvailability();
  }

  private submitAutosave() {
    this.form?.requestSubmit();
  }
}
